import { isDefined } from "@clipboard-health/util-ts";
import { logEvent } from "@src/appV2/lib/analytics";
import {
  useGetWorkerTrips,
  type WorkerTripData,
} from "@src/appV2/redesign/DailyView/api/useGetWorkerTrips";
import { useEffect, useState } from "react";

import { NO_SHOW_WARNING_ANALYTICS_EVENTS } from "../analyticsEvents";
import { getNoShowWarningType, type NoShowWarningType } from "../utils/getNoShowWarningType";

interface UseNoShowWarningParams {
  workplaceId: string | undefined;
  shiftId: string;
  workerId: string;
  shiftStart: string | undefined;
  enabled: boolean;
}

interface UseNoShowWarningResult {
  step: "loading" | "warning" | "form";
  warningType: NoShowWarningType | null;
  tripData: WorkerTripData | undefined;
  /** Call when "Confirm No Show" is clicked on the warning step. Logs CONFIRMED and advances to the form. */
  handleWarningConfirm: () => void;
  /**
   * Call from your "Back" button handler on the warning step.
   * Logs DISMISSED (action="back") and resets warning state.
   * You are responsible for calling your own onCancel() after this.
   */
  onWarningBack: () => void;
  /**
   * Call from your modal close/X-button handler.
   * If currently on the warning step, logs DISMISSED (action="close") and resets warning state.
   * No-op when already on the form step (avoids a spurious VIEWED re-fire on close).
   * You are responsible for calling your own onCancel() after this.
   */
  onWarningClose: () => void;
  /** Manually resets warningAcknowledged — call when the modal re-opens to restart the warning flow. */
  resetWarning: () => void;
}

export function useNoShowWarning(params: UseNoShowWarningParams): UseNoShowWarningResult {
  const { workplaceId, shiftId, workerId, shiftStart, enabled } = params;

  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  const isQueryEnabled = enabled && isDefined(workplaceId);
  const { isLoading: isLoadingTrip, tripsByShiftId } = useGetWorkerTrips(
    { workplaceId: workplaceId ?? "", tripIds: [{ shiftId, workerId }] },
    { enabled: isQueryEnabled }
  );

  const tripData = tripsByShiftId.get(shiftId);
  const warningType = getNoShowWarningType(tripData, shiftStart);

  function deriveStep(): "loading" | "warning" | "form" {
    if (isQueryEnabled && isLoadingTrip && !warningAcknowledged) {
      return "loading";
    }
    if (!warningAcknowledged && isDefined(warningType)) {
      return "warning";
    }
    return "form";
  }
  const step = deriveStep();

  const analyticsProps = { warningType, shiftId, workerId, workplaceId };

  useEffect(() => {
    if (step === "warning" && isDefined(warningType)) {
      logEvent(NO_SHOW_WARNING_ANALYTICS_EVENTS.VIEWED, analyticsProps);
    }
    // analyticsProps is reconstructed each render — listing individual stable primitives avoids
    // spurious re-fires while still capturing the values at the time the step changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function handleWarningConfirm(): void {
    logEvent(NO_SHOW_WARNING_ANALYTICS_EVENTS.CONFIRMED, analyticsProps);
    setWarningAcknowledged(true);
  }

  function onWarningBack(): void {
    logEvent(NO_SHOW_WARNING_ANALYTICS_EVENTS.DISMISSED, { action: "back", ...analyticsProps });
    setWarningAcknowledged(false);
  }

  function onWarningClose(): void {
    if (step === "warning") {
      logEvent(NO_SHOW_WARNING_ANALYTICS_EVENTS.DISMISSED, { action: "close", ...analyticsProps });
      // Only reset when on the warning step — resetting from the form step would cause
      // step to briefly flip back to "warning", firing a spurious VIEWED event.
      // WorkerNoShowModal resets via useEffect([visible]) on re-open.
      setWarningAcknowledged(false);
    }
  }

  function resetWarning(): void {
    setWarningAcknowledged(false);
  }

  return {
    step,
    warningType,
    tripData,
    handleWarningConfirm,
    onWarningBack,
    onWarningClose,
    resetWarning,
  };
}
